import React, { Suspense, lazy } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Spinner } from "@shopify/polaris";
import {
  PRODUCT_SETTINGS_APPLICATIONS_PATH,
  PRODUCT_SETTINGS_PLANS_AND_USAGE_PATH,
} from "routes/shared/coreRoutes";

const LazyGlobalSettingsPage = lazy(() =>
  import(
    /* webpackChunkName: "route-core-global-settings" */ "containers/GlobalSettingsPage/GlobalSettingsPage"
  ),
);

/**
 * `/profile` is an alias of the Global settings hub.
 * Legacy deep-links (tab / hash / state) still redirect to the matching new route;
 * plain `/profile` renders the same screen as `/global-settings` so old callers keep working.
 */
function resolveProfileRedirect({ search, hash, state }) {
  const params = new URLSearchParams(search || "");
  const closeParam = params.get("close");
  if (closeParam === "True" || closeParam === "true" || closeParam === "1") {
    return "/profile/sso_complete";
  }

  if (state?.apps_marketplace === true) {
    return PRODUCT_SETTINGS_APPLICATIONS_PATH;
  }

  if (hash === "#membership-details") {
    return PRODUCT_SETTINGS_PLANS_AND_USAGE_PATH;
  }

  const tab = params.get("tab");
  switch (tab) {
    case "personal-settings":
      return "/personal-settings";
    case "company-settings":
      return "/business-settings?tab=business";
    case "referral":
    case "referral-program":
      return "/business-settings?tab=referral";
    case "applications":
    case "l-workspace-marketplace":
      return PRODUCT_SETTINGS_APPLICATIONS_PATH;
    case "team":
      return "/business-settings?tab=team";
    case "team-security":
    case "sso":
      return "/business-settings?tab=sso";
    default:
      // Same screen as /global-settings — keep URL as /profile
      return null;
  }
}

function GlobalSettingsFallback() {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
      <Spinner size="large" />
    </div>
  );
}

export default function ProfileRedirect() {
  const location = useLocation();
  const to = resolveProfileRedirect(location);

  if (!to) {
    return (
      <Suspense fallback={<GlobalSettingsFallback />}>
        <LazyGlobalSettingsPage />
      </Suspense>
    );
  }

  return <Navigate to={to} replace state={location.state} />;
}
